import { useState } from "react";
import syrupLogo from "./syrup-logo.svg";
import { T, fontDisplay } from "./theme";
import { supabase } from "./lib/supabase";

/**
 * Set your own password before going any further.
 *
 * The password that got you in was generated by an admin and sent to you in a
 * message, which means at least two people have seen it and it is sitting in
 * whatever they sent it through. This closes that window on first use rather
 * than trusting anyone to get round to it.
 */
export default function SetPassword({ email, onDone }) {
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = pw.length > 0 && pw.length < 10;
  const mismatch = again.length > 0 && pw !== again;
  const ready = pw.length >= 10 && pw === again;

  const save = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { error: e1 } = await supabase.auth.updateUser({ password: pw });
      if (e1) throw new Error(e1.message);
      const { data } = await supabase.auth.getUser();
      if (data?.user?.id) {
        await supabase
          .from("profiles")
          .update({ must_change_password: false, updated_at: new Date().toISOString() })
          .eq("id", data.user.id);
      }
      await onDone();
    } catch (e) {
      setError(e.message || String(e));
      setBusy(false);
    }
  };

  const field = {
    width: "100%",
    fontSize: 16, // 16px keeps iOS from zooming the page on focus
    padding: "12px 14px",
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    marginTop: 8,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Manrope', system-ui, sans-serif",
        color: T.ink,
      }}
    >
      <div style={{ width: "min(400px, 100%)" }}>
        <img src={syrupLogo} alt="Syrup" style={{ height: 40, marginBottom: 20 }} />
        <h1 style={{ fontFamily: fontDisplay, fontSize: 24, margin: "0 0 6px" }}>
          Choose a password
        </h1>
        <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginTop: 0 }}>
          The one you were sent was generated for you and has been seen by whoever set up your
          account. Pick your own and it stops working.
        </p>
        {email && (
          <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Signing in as {email}</div>
        )}

        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="New password"
          autoFocus
          style={field}
        />
        <input
          type="password"
          value={again}
          onChange={(e) => setAgain(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          placeholder="Again, to be sure"
          style={field}
        />

        <div style={{ fontSize: 11, color: tooShort || mismatch ? T.cost : T.muted, marginTop: 8 }}>
          {mismatch
            ? "Those two do not match."
            : tooShort
              ? "At least ten characters."
              : "At least ten characters. Longer beats complicated."}
        </div>

        {error && <div style={{ color: T.cost, fontSize: 12, marginTop: 8 }}>{error}</div>}

        <button
          onClick={save}
          disabled={!ready || busy}
          style={{
            width: "100%",
            marginTop: 14,
            padding: "12px 14px",
            borderRadius: 8,
            border: "none",
            background: ready ? T.accent : T.border,
            color: ready ? "#fff" : T.muted,
            fontSize: 15,
            cursor: ready ? "pointer" : "default",
            fontFamily: "inherit",
          }}
        >
          {busy ? "Saving…" : "Save and carry on"}
        </button>

        {/* The only screen in the app with no navigation. Someone who signed in
            as the wrong account, or who does not want to set a password now,
            had nothing to do but close the tab — and the session would still be
            there when they came back. */}
        <button
          onClick={async () => {
            await supabase.auth.signOut({ scope: "local" }).catch(() => {});
            supabase.auth.signOut().catch(() => {});
            window.location.replace("/");
          }}
          style={{
            width: "100%",
            marginTop: 10,
            padding: "10px 14px",
            borderRadius: 8,
            border: "none",
            background: "transparent",
            color: T.muted,
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
